import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ReportNameEnum } from "../utils/enums";
import { parseISO, format, formatISO, subMonths, subDays, startOfMonth, endOfMonth } from "date-fns";

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [reportName, setReportName] = useState("MIS"); 
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingReports, setFetchingReports] = useState(true);
  const [latestReport, setLatestReport] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterReportType, setFilterReportType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'report_generated_date_time', direction: 'desc' });
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setFetchingReports(true);
    try {
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_FETCH_ALL_REPORTS}`;

      const params = new URLSearchParams({
        merchantId: merchantId,
      }).toString();


      const response = await axios.get(`${apiUrl}?${params}`, {
        headers: {
          'Authorization': authToken,
          'x-merchant-id': merchantId
        }
      });

      const arr = Array.isArray(response.data) ? response.data : [];
      const sortedReports = arr
         .slice()
         .sort((a, b) =>
           new Date(b.report_generated_date_time) - new Date(a.report_generated_date_time)
         );

      setReports(sortedReports);
      if (sortedReports.length > 0) {
        setLatestReport(sortedReports[0]);
      } else {
        setLatestReport(null);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to fetch reports");
    } finally {
      setFetchingReports(false);
    }
  };

  const generateReport = async (e) => {
    e.preventDefault(); 
    
    // Validation
    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From Date cannot be after To Date");
      return;
    }
    
    setLoading(true);

    const authToken = localStorage.getItem("authToken");
    const merchantId = localStorage.getItem("merchantId");

    const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_REPORT_ENDPOINT}`;

    const params = new URLSearchParams({
      reportName: reportName,
      fromDate: fromDate,
      toDate: toDate,
    }).toString();

    try {
      const response = await axios.get(`${apiUrl}?${params}`, {
        headers: {
          'Authorization': authToken,
          'x-merchant-id': merchantId
        }
      });

      toast.success("Report generated successfully!");
      fetchReports(); 
      setFromDate("");
      setToDate("");
    } catch (error) {
      toast.error("Failed to generate report.");
      console.error("Error:", error);
    }
    setLoading(false);
};

const formatDateTime = (isoString) => {
  return format(parseISO(isoString), "dd MMM yyyy, hh:mm:ss a")
    .replace("AM", "am")
    .replace("PM", "pm");
};

const setDatePreset = (preset) => {
  const today = new Date();
  let from, to;

  switch (preset) {
    case 'today':
      from = to = format(today, 'yyyy-MM-dd');
      break;
    case 'yesterday':
      from = to = format(subDays(today, 1), 'yyyy-MM-dd');
      break;
    case 'last7days':
      from = format(subDays(today, 7), 'yyyy-MM-dd');
      to = format(today, 'yyyy-MM-dd');
      break;
    case 'last30days':
      from = format(subDays(today, 30), 'yyyy-MM-dd');
      to = format(today, 'yyyy-MM-dd');
      break;
    case 'thisMonth':
      from = format(startOfMonth(today), 'yyyy-MM-dd');
      to = format(endOfMonth(today), 'yyyy-MM-dd');
      break;
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      from = format(startOfMonth(lastMonth), 'yyyy-MM-dd');
      to = format(endOfMonth(lastMonth), 'yyyy-MM-dd');
      break;
    default:
      return;
  }

  setFromDate(from);
  setToDate(to);
};

const handleDownload = async (report) => {
  setDownloadingId(report.report_generated_date_time);
  try {
    window.open(report.report_url, '_blank');
    toast.success("Download started");
  } catch (error) {
    toast.error("Failed to download report");
  } finally {
    setTimeout(() => setDownloadingId(null), 1000);
  }
};

// Statistics calculation
const stats = useMemo(() => {
  const totalReports = reports.length;
  const uniqueReportTypes = [...new Set(reports.map(r => r.report_name))].length;
  const reportsThisMonth = reports.filter(r => {
    const reportDate = new Date(r.report_generated_date_time);
    const now = new Date();
    return reportDate.getMonth() === now.getMonth() && reportDate.getFullYear() === now.getFullYear();
  }).length;
  
  return {
    total: totalReports,
    types: uniqueReportTypes,
    thisMonth: reportsThisMonth,
    latestDate: reports.length > 0 ? formatDateTime(reports[0].report_generated_date_time) : 'N/A'
  };
}, [reports]);

// Filtered and sorted reports
const filteredAndSortedReports = useMemo(() => {
  let filtered = reports.filter(report => {
    const matchesSearch = 
      report.report_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.report_generated_by.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.generated_from_date.includes(searchQuery) ||
      report.generated_to_date.includes(searchQuery);
    
    const matchesFilter = filterReportType === 'all' || report.report_name === filterReportType;
    
    return matchesSearch && matchesFilter;
  });

  // Sorting
  filtered.sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === 'report_generated_date_time') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  return filtered;
}, [reports, searchQuery, filterReportType, sortConfig]);

// Pagination
const paginatedReports = useMemo(() => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  return filteredAndSortedReports.slice(startIndex, startIndex + itemsPerPage);
}, [filteredAndSortedReports, currentPage, itemsPerPage]);

const totalPages = Math.ceil(filteredAndSortedReports.length / itemsPerPage);

const handleSort = (key) => {
  setSortConfig(prev => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
  }));
};

const uniqueReportTypes = useMemo(() => {
  return [...new Set(reports.map(r => r.report_name))];
}, [reports]);

return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 mt-2">
    <div className="container mx-auto max-w-[1600px] px-2">
      
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Reports Dashboard
            </h3>
            <p className="text-gray-600">Generate, manage and download your shipment orders reports</p>
          </div>
          <button
            onClick={fetchReports}
            disabled={fetchingReports}
            className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-5 h-5 ${fetchingReports ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">Total Reports</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-blue-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium mb-1">Report Types</p>
              <p className="text-xl font-bold">{stats.types}</p>
            </div>
            <div className="bg-green-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium mb-1">This Month</p>
              <p className="text-xl font-bold">{stats.thisMonth}</p>
            </div>
            <div className="bg-purple-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition-transform duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium mb-1">Latest Report</p>
              <p className="text-xs font-medium mt-1">{stats.latestDate.split(',')[0]}</p>
            </div>
            <div className="bg-orange-400 bg-opacity-50 rounded-full p-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Report Generation Form */}
      <form
        onSubmit={generateReport}
        className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
      >
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-2">
          <h3 className="text-xl font-semibold text-white flex items-center gap-2">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Generate New Report
          </h3>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-2 text-sm">Report Type</label>
              <select
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
              >
                {Object.values(ReportNameEnum).map((report) => (
                  <option key={report.id} value={report.name}>
                    {report.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2 text-sm">From Date</label>
              <input
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                max={toDate || undefined}
                required
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2 text-sm">To Date</label>
              <input
                type="date"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-white shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                min={fromDate || undefined}
                required
              />
            </div>
          </div>

          {/* Date Presets */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 font-medium mb-3">Quick Date Presets:</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Today', value: 'today' },
                { label: 'Yesterday', value: 'yesterday' },
                { label: 'Last 7 Days', value: 'last7days' },
                { label: 'Last 30 Days', value: 'last30days' },
                { label: 'This Month', value: 'thisMonth' },
                { label: 'Last Month', value: 'lastMonth' }
              ].map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => setDatePreset(preset.value)}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-700 rounded-lg transition-all duration-200 border border-gray-200 hover:border-blue-300"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Latest Report Card */}
      {latestReport && (
        <div className="mb-8 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl shadow-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Latest Generated Report
            </h2>
            <span className="px-3 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">New</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 font-medium mb-1">Report Type</p>
              <p className="text-lg font-semibold text-gray-800">{latestReport.report_name}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 font-medium mb-1">Generated By</p>
              <p className="text-lg font-semibold text-gray-800">{latestReport.report_generated_by}</p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 font-medium mb-1">Duration</p>
              <p className="text-lg font-semibold text-gray-800">{latestReport.generated_from_date} to {latestReport.generated_to_date}</p>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
            <p className="text-xs text-gray-500 font-medium mb-1">Generated On</p>
            <p className="text-base font-semibold text-gray-800">{formatDateTime(latestReport.report_generated_date_time)}</p>
          </div>

          {latestReport.report_url && (
            <button
              onClick={() => handleDownload(latestReport)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Latest Report
            </button>
          )}
        </div>
      )}

      {/* Reports Table Section */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Table Header with Search and Filters */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <h2 className="text-xl font-bold text-gray-800">All Reports History</h2>
            
            <div className="flex flex-col sm:flex-row gap-3 flex-1 lg:max-w-2xl">
              {/* Search Bar */}
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
                />
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Filter Dropdown */}
              <select
                value={filterReportType}
                onChange={(e) => {
                  setFilterReportType(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none"
              >
                <option value="all">All Types</option>
                {uniqueReportTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {paginatedReports.length} of {filteredAndSortedReports.length} reports
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {fetchingReports ? (
            <div className="p-12 text-center">
              <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-600 font-medium">Loading reports...</p>
            </div>
          ) : paginatedReports.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    #
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_name')}
                  >
                    <div className="flex items-center gap-2">
                      Report Name
                      {sortConfig.key === 'report_name' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Duration
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_generated_date_time')}
                  >
                    <div className="flex items-center gap-2">
                      Generated Date
                      {sortConfig.key === 'report_generated_date_time' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('report_generated_by')}
                  >
                    <div className="flex items-center gap-2">
                      Generated By
                      {sortConfig.key === 'report_generated_by' && (
                        <svg className={`w-4 h-4 transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedReports.map((report, index) => (
                  <tr key={`${report.report_generated_date_time}-${index}`} className="hover:bg-blue-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-semibold rounded-full">
                        {report.report_name}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">From: {report.generated_from_date}</span>
                        <span className="text-xs text-gray-500">To: {report.generated_to_date}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {formatDateTime(report.report_generated_date_time)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                      {report.report_generated_by}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleDownload(report)}
                        disabled={downloadingId === report.report_generated_date_time}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadingId === report.report_generated_date_time ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500 text-lg font-medium mb-2">No Reports Found</p>
              <p className="text-gray-400 text-sm">Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 font-medium">Rows per page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 border-2 border-gray-200 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 outline-none text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm"
                >
                  Next
                </button>
              </div>

              <div className="text-sm text-gray-600">
                Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default Reports;